import {
  ActionIcon,
  Box,
  Group,
  Popover,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@cowbell/ui/core';
import {
  faArrowUp,
  faMicrophone,
  faPlus,
  faSparkles,
  faStop,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type { SlashCommand } from '../../types/transport';
import classes from './Composer.module.css';
import { IMPROVE_REVEAL_MS, useImproveReveal } from './use-improve-reveal';
import { PixieDust } from './PixieDust';

/** Fallback row cap for the auto-grow when the stylesheet gives the textarea
 *  no max-height (the CSS cap is 40vh and wins when present). */
const MAX_ROWS = 12;

/** Matches a slash-command token being typed at the end of the draft, e.g.
 *  "hello /sum" → captures "sum". Requires the slash to start at the
 *  beginning of the draft or after whitespace so mid-word slashes (URLs,
 *  paths) don't trigger the picker. Shared by the trigger-detection effect
 *  and `handleCommandSelect` so both locate the same slash. */
const SLASH_TRIGGER_REGEX = /(?:^|\s)\/(\S*)$/;

const REGEXP_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;
function escapeForRegExp(text: string): string {
  return text.replace(REGEXP_SPECIAL_CHARS, String.raw`\$&`);
}

export interface ComposerProps {
  readonly value: string;
  readonly onChange: (text: string) => void;
  readonly onSubmit: (text: string, skillId?: string) => void;
  readonly onCancel?: () => void;
  /** Click handler for the sparkle (improve-prompt) button. When omitted,
   *  the button is hidden — backward-compatible with consumers that haven't
   *  wired improve-prompt yet. */
  readonly onImprove?: () => void;
  readonly disabled?: boolean;
  /** Name of the active agent, used to personalise the placeholder. */
  readonly agentName?: string;
  readonly isStreaming?: boolean;
  /** True while an `onImprove` call is in flight. Disables Send and spins
   *  the sparkle icon until the improved prompt is returned and rendered. */
  readonly isImproving?: boolean;
  /** Optional UI rendered inside the composer's bordered container, above
   *  the input row — for context chips. Kept as a slot (instead of
   *  importing `<ContextChipList />` directly) so the standalone Composer
   *  doesn't depend on `useShellContexts` and stays unit-testable without
   *  a `MooChatProvider`. */
  readonly chipsSlot?: ReactNode;
  /** Rendered inline on the bottom action row, after the attach/voice icons.
   *  For controls that belong with the composer's own affordances rather than
   *  stacked above the input like `chipsSlot`. */
  readonly actionsSlot?: ReactNode;
  /** Click handler for the paperclip (file attach) button. When omitted,
   *  the button is hidden. Called with the user's `FileList` converted to
   *  an array — empty arrays are filtered out by the Composer. */
  readonly onAttachFiles?: (files: File[]) => void;
  /** MIME / extension filter for the file picker. Defaults to no filter
   *  (any file). */
  readonly attachAccept?: string;
  /** Called when the user clicks the mic button to toggle recording on/off. */
  readonly onVoiceInput?: () => void;
  /** True once the server is ready and mic audio is actively streaming. */
  readonly isVoiceListening?: boolean;
  /** True while connecting to the transcription service (before ready). */
  readonly isVoiceConnecting?: boolean;
  /** Running transcript shown as italic preview during recording. */
  readonly liveTranscript?: string;
  /** Stops any active recording and returns its trimmed transcript. Called
   *  on Send/Enter while recording so in-progress dictation is folded into
   *  the message instead of being silently dropped. */
  readonly consumeTranscript?: () => string;
  /** Slash commands for the active agent. When absent or empty, no listbox renders. */
  readonly slashCommands?: SlashCommand[];
  /** Guessed next prompt. Shown as the placeholder while the composer is
   *  empty; Tab fills it in, Esc calls `onDismissSuggestion`. */
  readonly suggestion?: string;
  readonly onDismissSuggestion?: () => void;
}

export function Composer({
  value,
  onChange,
  onSubmit,
  onCancel,
  onImprove,
  disabled = false,
  agentName,
  isStreaming = false,
  isImproving = false,
  chipsSlot,
  actionsSlot,
  onAttachFiles,
  attachAccept,
  onVoiceInput,
  isVoiceListening = false,
  isVoiceConnecting = false,
  liveTranscript = '',
  consumeTranscript,
  slashCommands,
  suggestion,
  onDismissSuggestion,
}: ComposerProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // While recording, an in-progress dictation with no typed draft yet
  // should still be sendable — it gets folded into the message at submit
  // time (see `buildSubmitText`).
  const hasDictation =
    (isVoiceListening || isVoiceConnecting) && liveTranscript.trim().length > 0;
  const canSubmit =
    (value.trim().length > 0 || hasDictation) &&
    !disabled &&
    !isStreaming &&
    !isImproving;
  const canImprove =
    value.trim().length > 0 && !disabled && !isStreaming && !isImproving;
  const isVoiceActive = isVoiceListening || isVoiceConnecting;
  const showSuggestion =
    Boolean(suggestion) &&
    value === '' &&
    !disabled &&
    !isStreaming &&
    !isImproving &&
    !isVoiceActive;
  const suggestionHintId = useId();
  let placeholder = agentName ? `Ask ${agentName}…` : 'Ask anything…';
  if (isVoiceActive) placeholder = '';
  else if (showSuggestion && suggestion) placeholder = suggestion;

  // Folds any in-progress dictation into the draft and stops the mic, so a
  // single Send/Enter both ends recording and submits the complete text.
  const buildSubmitText = useCallback(() => {
    if (!hasDictation) return value;
    const dictated = consumeTranscript?.().trim() ?? '';
    if (!dictated) return value;
    const draft = value.trim();
    return draft ? `${draft} ${dictated}` : dictated;
  }, [hasDictation, consumeTranscript, value]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const improveRevealing = useImproveReveal(isImproving, value);

  // `chipsSlot` is host-supplied and often a fragment of chip components
  // (upload chips, context chips) that each render null when they have
  // nothing to show — so the slot can be a non-null element yet render no
  // visible content. Measuring the wrapper's actual children (rather than
  // trusting `Boolean(chipsSlot)`) is what lets the textarea get its own
  // breathing room back when nothing is really there. Runs after every
  // render (no dependency array) since chip content can change independent
  // of any prop identity this component would otherwise depend on.
  const chipsWrapRef = useRef<HTMLDivElement>(null);
  const [hasChips, setHasChips] = useState(false);
  useLayoutEffect(() => {
    setHasChips((chipsWrapRef.current?.childElementCount ?? 0) > 0);
  });

  // Slash-command listbox state
  const [commandQuery, setCommandQuery] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Grow the textarea to fit its content, capped at the stylesheet's
  // max-height (40vh) or MAX_ROWS, then scroll. The pixel height is set from
  // a measurement and refit on resize, so it tracks the root font size.
  const fitToContent = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const styles = globalThis.getComputedStyle(el);
    const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
    const paddingY =
      (Number.parseFloat(styles.paddingTop) || 0) +
      (Number.parseFloat(styles.paddingBottom) || 0);
    const borderY =
      (Number.parseFloat(styles.borderTopWidth) || 0) +
      (Number.parseFloat(styles.borderBottomWidth) || 0);
    const cssMax = Number.parseFloat(styles.maxHeight);
    const maxHeight = Number.isFinite(cssMax)
      ? cssMax
      : lineHeight * MAX_ROWS + paddingY + borderY;

    // Browsers leave placeholder text out of `scrollHeight`, so an empty
    // field showing a long placeholder (a guessed next prompt) would stay one
    // row and clip it. Measure the placeholder as the value instead, then put
    // the empty value back. This runs in a layout effect, before paint, and
    // fires no input event, so the swap is never seen.
    const measurePlaceholder = el.value === '' && el.placeholder !== '';
    if (measurePlaceholder) el.value = el.placeholder;
    el.style.height = 'auto';
    const contentHeight = el.scrollHeight;
    if (measurePlaceholder) el.value = '';
    const next = Math.min(contentHeight, maxHeight);
    el.style.height = `${next}px`;
    el.style.overflowY =
      !measurePlaceholder && contentHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  // Re-fit whenever the value changes — typing, or an improved prompt being
  // written back in. An emptied composer shrinks back to one row.
  useLayoutEffect(() => {
    fitToContent();
  }, [value, placeholder, fitToContent]);

  // Point screen readers at the Tab hint while a suggestion shows. Set on the
  // element directly: Mantine's Textarea derives `aria-describedby` from its
  // own description/error ids and drops a passed-in value.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (showSuggestion) el.setAttribute('aria-describedby', suggestionHintId);
    else el.removeAttribute('aria-describedby');
  }, [showSuggestion, suggestionHintId]);

  // Keep the auto-fit correct when a viewport width change re-wraps the text.
  useEffect(() => {
    globalThis.addEventListener('resize', fitToContent);
    return () => globalThis.removeEventListener('resize', fitToContent);
  }, [fitToContent]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.currentTarget.value);
    },
    [onChange]
  );

  // Slash-command trigger detection — runs when value or slashCommands changes
  useEffect(() => {
    if (!slashCommands || slashCommands.length === 0) {
      setCommandQuery(null);
      return;
    }
    const match = SLASH_TRIGGER_REGEX.exec(value);
    const q = match ? match[1] : null;
    setCommandQuery(q);
    if (q !== null) setHighlightedIndex(0);
  }, [value, slashCommands]);

  const filteredCommands = useMemo(() => {
    if (!slashCommands || commandQuery === null) return [];
    const q = commandQuery.toLowerCase();
    return slashCommands.filter((c) => c.name.toLowerCase().startsWith(q));
  }, [slashCommands, commandQuery]);

  const listboxOpen = commandQuery !== null && filteredCommands.length > 0;

  // Clamp highlightedIndex when the filtered list shrinks (e.g. user keeps typing
  // and the previous index now points past the end).
  useEffect(() => {
    if (filteredCommands.length > 0) {
      setHighlightedIndex((i) => Math.min(i, filteredCommands.length - 1));
    }
  }, [filteredCommands]);

  // Derive argHint from value — computed so it survives re-renders with the post-command value.
  // Matches case-insensitively, anywhere in the draft (not just at the start) so a
  // command inserted mid-message by the listbox still attaches its skillId. Requires
  // a trailing space (args may follow or not) so a bare "/name" the user is still
  // typing — with no space yet — isn't treated as an active, submittable command.
  const activeCommand = useMemo(() => {
    if (!slashCommands || slashCommands.length === 0) return null;
    const valueLower = value.toLowerCase();
    return (
      slashCommands.find((cmd) => {
        const name = escapeForRegExp(cmd.name.toLowerCase());
        return new RegExp(String.raw`(?:^|\s)/${name}\s`).test(valueLower);
      }) ?? null
    );
  }, [value, slashCommands]);

  const argHint = activeCommand?.argHint ?? null;

  // Show the argHint only while the user has typed nothing past the command's
  // trailing space — i.e. value ends with exactly "/<name> " with no further text.
  let argHintVisible = false;
  if (argHint !== null && activeCommand !== null) {
    const cmdToken = `/${activeCommand.name.toLowerCase()} `;
    const lv = value.toLowerCase();
    const idx = lv.lastIndexOf(cmdToken);
    argHintVisible = idx !== -1 && idx + cmdToken.length === lv.length;
  }

  const handleCommandSelect = useCallback(
    (cmd: SlashCommand) => {
      const match = SLASH_TRIGGER_REGEX.exec(value);
      if (!match) return;
      // Use the matched slash's own position rather than value.lastIndexOf('/') —
      // the typed token (\S*) can itself contain a "/" (e.g. "/foo/bar"), which
      // would make lastIndexOf find that inner slash and splice the wrong segment.
      const slashIdx = match.index + match[0].indexOf('/');
      const newValue = value.slice(0, slashIdx) + '/' + cmd.name + ' ';
      onChange(newValue);
      setCommandQuery(null);
      textareaRef.current?.focus();
    },
    [value, onChange]
  );

  // Handles keys while the slash-command listbox is open. Returns true when
  // it consumed the key, so `handleKeyDown` skips its own Enter-to-send logic.
  const handleListboxKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((i) => (i + 1) % filteredCommands.length);
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(
          (i) => (i - 1 + filteredCommands.length) % filteredCommands.length
        );
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const cmd = filteredCommands[highlightedIndex];
        if (cmd) handleCommandSelect(cmd);
        return true;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setCommandQuery(null);
        return true;
      }
      return false;
    },
    [filteredCommands, highlightedIndex, handleCommandSelect]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (listboxOpen && handleListboxKeyDown(e)) return;
      if (showSuggestion && suggestion) {
        const plainTab =
          e.key === 'Tab' &&
          !e.shiftKey &&
          !e.altKey &&
          !e.ctrlKey &&
          !e.metaKey;
        if (plainTab) {
          e.preventDefault();
          onChange(suggestion);
          return;
        }
        if (e.key === 'Escape') {
          // The Ask Moo shell closes the whole sidebar on a window-level
          // Escape; this one only dismisses the guess.
          e.preventDefault();
          e.stopPropagation();
          onDismissSuggestion?.();
          return;
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (canSubmit) onSubmit(buildSubmitText(), activeCommand?.skillId);
      }
    },
    [
      listboxOpen,
      handleListboxKeyDown,
      showSuggestion,
      suggestion,
      onChange,
      onDismissSuggestion,
      canSubmit,
      onSubmit,
      buildSubmitText,
      activeCommand,
    ]
  );

  const handleSubmit = useCallback(() => {
    if (canSubmit) {
      onSubmit(buildSubmitText(), activeCommand?.skillId);
    }
  }, [canSubmit, onSubmit, buildSubmitText, activeCommand]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  const handleImprove = useCallback(() => {
    if (onImprove && canImprove) {
      onImprove();
    }
  }, [onImprove, canImprove]);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFilesPicked = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = e.currentTarget.files;
      if (!list || list.length === 0) return;
      const files = Array.from(list);
      onAttachFiles?.(files);
      // Reset so the same file can be re-picked without restart.
      e.currentTarget.value = '';
    },
    [onAttachFiles]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!onAttachFiles) return;
      const files = Array.from(e.clipboardData.items)
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
      if (files.length === 0) return;
      e.preventDefault();
      onAttachFiles(files);
    },
    [onAttachFiles]
  );

  let micLabel = 'Record voice input';
  if (isVoiceListening) {
    micLabel = 'Listening — click to stop';
  } else if (isVoiceConnecting) {
    micLabel = 'Connecting… click to cancel';
  }

  return (
    <Box className={classes.footer}>
      <Popover
        opened={listboxOpen}
        position="top-start"
        width="target"
        trapFocus={false}
        // Fullscreen and floating layout wrappers set overflow: hidden on the
        // chat shell; render in the default document.body portal so the
        // listbox isn't clipped in those modes (V1 parity for sidebar mode
        // still works the same since portalled popovers position via the
        // floating-ui middleware, not DOM nesting).
        // zIndex must exceed the floating panel (PANEL_Z = 9000) so the
        // listbox is not hidden behind it in floating/fullscreen modes.
        // floatingStrategy="fixed" forces position:fixed on the dropdown so
        // its stacking is always viewport-relative (avoids absolute-position
        // containing-block ambiguity when the panel creates a stacking context).
        transitionProps={{ duration: 0 }}
        zIndex={1300}
        floatingStrategy="fixed"
      >
        <Popover.Target>
          <Box className={classes.composer}>
            <div ref={chipsWrapRef} className={classes.tokenRow}>
              {chipsSlot}
            </div>
            <Box
              className={`${classes.textareaWrap} ${
                hasChips ? classes.textareaWrapChips : ''
              }`}
              data-improving={isImproving || undefined}
              data-revealing={improveRevealing || undefined}
              data-suggesting={showSuggestion || undefined}
              style={
                {
                  '--improve-reveal-ms': `${IMPROVE_REVEAL_MS}ms`,
                } as CSSProperties
              }
            >
              <Textarea
                ref={textareaRef}
                variant="unstyled"
                rows={1}
                classNames={{ input: classes.input }}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={placeholder}
                // Read-only, not disabled, while improving: a disabled field
                // greys the prompt out; this one stays legible under the dust.
                disabled={disabled}
                readOnly={isImproving || isVoiceListening || isVoiceConnecting}
                aria-busy={isImproving || undefined}
                aria-label="Message input"
              />
              {isImproving ? (
                <PixieDust text={value} textareaRef={textareaRef} />
              ) : null}
              {showSuggestion && (
                <Box className={classes.suggestionHint}>
                  <kbd className={classes.suggestionKey} aria-hidden>
                    Tab
                  </kbd>
                  <span id={suggestionHintId} hidden>
                    Press Tab to use the suggestion
                  </span>
                </Box>
              )}
              {(isVoiceListening || isVoiceConnecting) && liveTranscript && (
                <Box className={classes.liveTranscript} aria-live="polite">
                  {liveTranscript}
                </Box>
              )}
            </Box>
            <Box className={classes.actionRow}>
              <Box className={classes.actionLeft}>
                {onAttachFiles && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={attachAccept}
                      onChange={handleFilesPicked}
                      style={{ display: 'none' }}
                      aria-hidden="true"
                      tabIndex={-1}
                      data-testid="composer-file-input"
                    />
                    <Tooltip label="Attach files">
                      <ActionIcon
                        className={classes.actionIcon}
                        variant="subtle"
                        color="gray"
                        onClick={handleAttachClick}
                        disabled={disabled || isImproving}
                        aria-label="Attach files"
                        size="lg"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </ActionIcon>
                    </Tooltip>
                  </>
                )}
                {onVoiceInput !== undefined && (
                  <Tooltip label={micLabel}>
                    <ActionIcon
                      variant={isVoiceListening ? 'filled' : 'subtle'}
                      color={isVoiceListening ? 'blue' : 'gray'}
                      className={`${classes.actionIcon} ${
                        isVoiceListening ? classes.micActive : ''
                      }`}
                      aria-pressed={isVoiceListening || isVoiceConnecting}
                      aria-label={micLabel}
                      onClick={onVoiceInput}
                      size="lg"
                    >
                      <FontAwesomeIcon icon={faMicrophone} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {actionsSlot}
              </Box>
              <Box className={classes.actionRight}>
                {onImprove && (
                  <Tooltip label="Improve prompt">
                    <ActionIcon
                      className={`${classes.actionIcon} ${classes.improveIcon}`}
                      data-idle={value.trim().length === 0 || undefined}
                      variant="light"
                      onClick={handleImprove}
                      disabled={!canImprove}
                      aria-label="Improve prompt"
                      size="lg"
                    >
                      <FontAwesomeIcon
                        icon={faSparkles}
                        className={
                          isImproving ? classes.sparkleSpinning : undefined
                        }
                      />
                    </ActionIcon>
                  </Tooltip>
                )}
                {isStreaming ? (
                  <Tooltip label="Stop generating">
                    <ActionIcon
                      className={`${classes.sendRound} ${classes.stopRound}`}
                      variant="filled"
                      onClick={handleCancel}
                      aria-label="Cancel"
                      size="lg"
                    >
                      <FontAwesomeIcon icon={faStop} />
                    </ActionIcon>
                  </Tooltip>
                ) : (
                  <Tooltip label="Send">
                    <ActionIcon
                      className={`${classes.sendRound} ${classes.sendSend}`}
                      variant="filled"
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      aria-label="Send"
                      size="lg"
                    >
                      <FontAwesomeIcon icon={faArrowUp} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Box>
            </Box>
            {argHintVisible && <Box className={classes.argHint}>{argHint}</Box>}
          </Box>
        </Popover.Target>
        <Popover.Dropdown p={0}>
          <Stack gap={0} role="listbox" aria-label="Slash commands">
            {filteredCommands.map((cmd, i) => (
              <UnstyledButton
                key={cmd.name}
                role="option"
                aria-selected={i === highlightedIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCommandSelect(cmd);
                }}
                px="sm"
                py="xs"
                style={{
                  background:
                    i === highlightedIndex
                      ? 'var(--mantine-color-blue-0, #e7f5ff)'
                      : 'transparent',
                }}
              >
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={600}>
                    /{cmd.name}
                  </Text>
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cmd.description}
                  </Text>
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
}
